import React, { useState } from 'react';
import { useMalcolmContext } from '../../contexts/MalcolmContext';

const FeedItem = ({ item, onSelect, isSelected }) => {
  const { saveToWorkspace, deleteFeedItem } = useMalcolmContext();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSaveOptions, setShowSaveOptions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSave = async (category) => {
    setIsSaving(true);
    try {
      await saveToWorkspace(item.id, category);
      setShowSaveOptions(false);
      // TODO: Show success toast
    } catch (error) {
      console.error('Error saving item:', error);
      // TODO: Show error toast
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteFeedItem(item.id);
      setShowDeleteConfirm(false);
      // TODO: Show success toast
    } catch (error) {
      console.error('Error deleting item:', error);
      // TODO: Show error toast
    } finally {
      setIsDeleting(false);
    }
  };

  const getSourceLogo = (source) => {
    if (!source) return '📰';
    
    const logoMap = {
      'openai': '🤖',
      'anthropic': '🔸',
      'google': '🔍',
      'meta': '📘',
      'microsoft': '🪟',
      'huggingface': '🤗',
      'arxiv': '📄',
      'deeplearning_ai': '🎓',
      'mit': '🏛️'
    };
    
    return logoMap[source.company] || '📰';
  };

  const getSourceBadgeColor = (source) => {
    if (!source) return 'bg-stone-100 text-stone-600';
    
    const colorMap = {
      'openai': 'bg-emerald-100 text-emerald-700',
      'anthropic': 'bg-orange-100 text-orange-700',
      'google': 'bg-azure-100 text-azure-700',
      'meta': 'bg-azure-100 text-azure-700',
      'microsoft': 'bg-azure-100 text-azure-700',
      'huggingface': 'bg-yellow-100 text-yellow-700',
      'arxiv': 'bg-violet-100 text-violet-700',
      'deeplearning_ai': 'bg-indigo-100 text-indigo-700',
      'mit': 'bg-red-100 text-red-700'
    };
    
    return colorMap[source.company] || 'bg-stone-100 text-stone-600';
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}w ago`;
  };

  const getRelevanceBadge = (score) => {
    if (score >= 90) return { color: 'bg-emerald-100 text-emerald-700', label: 'Critical' };
    if (score >= 80) return { color: 'bg-azure-100 text-azure-700', label: 'High' };
    if (score >= 60) return { color: 'bg-yellow-100 text-yellow-700', label: 'Medium' };
    return { color: 'bg-stone-100 text-stone-600', label: 'Low' };
  };

  const getTrendingBadge = (score) => {
    if (score >= 80) return { icon: '🔥', label: 'Hot' };
    if (score >= 60) return { icon: '📈', label: 'Rising' };
    return null;
  };

  const relevanceBadge = getRelevanceBadge(item.relevance_score);
  const trendingBadge = getTrendingBadge(item.trending_score);

  // Determine border color based on item importance
  const getBorderColor = () => {
    if (item.relevance_score >= 90) return 'border-l-4 border-l-emerald-500';
    if (item.relevance_score >= 80) return 'border-l-4 border-l-azure-500';
    if (item.trending_score >= 80) return 'border-l-4 border-l-pink-500';
    if (item.tags?.includes('beta_access')) return 'border-l-4 border-l-violet-500';
    return 'border-l-4 border-l-transparent';
  };

  return (
    <div className={`
      bg-white rounded-lg border border-stone-200 hover:border-stone-300 
      transition-all duration-200 hover:shadow-md ${getBorderColor()}
      ${isSelected ? 'ring-2 ring-indigo-500 border-indigo-300' : ''}
    `}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-3 flex-1">
            {/* Source logo */}
            <div className={`
              w-10 h-10 rounded-lg flex items-center justify-center text-lg flex-shrink-0
              ${getSourceBadgeColor(item.source)}
            `}>
              {getSourceLogo(item.source)}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-slate-900 leading-tight mb-2 hover:text-indigo-600 cursor-pointer">
                <a 
                  href={item.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={onSelect}
                >
                  {item.title}
                </a>
              </h3>
              
              {/* Meta info */}
              <div className="flex items-center space-x-3 text-sm text-stone-500 mb-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSourceBadgeColor(item.source)}`}>
                  {item.source?.name || 'Unknown Source'}
                </span>
                <span>{formatTimeAgo(item.published_at || item.crawled_at)}</span>
                {item.relevance_score && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${relevanceBadge.color}`}>
                    {Math.round(item.relevance_score)}% Relevant
                  </span>
                )}
                {trendingBadge && (
                  <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded-full text-xs font-medium">
                    {trendingBadge.icon} {trendingBadge.label}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        {item.summary && (
          <div className="mb-4">
            <p className="text-stone-700 leading-relaxed">{item.summary}</p>
          </div>
        )}

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {item.tags.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-stone-100 text-stone-700 rounded-full text-xs font-medium"
              >
                {tag.replace('_', ' ')}
              </span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-stone-100">
          <div className="flex items-center space-x-3">
            {/* Save dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSaveOptions(!showSaveOptions)}
                disabled={isSaving}
                className="flex items-center space-x-2 px-3 py-2 border border-teal-300 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-50 transition-colors disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span>{isSaving ? 'Saving...' : 'Save'}</span>
              </button>

              {showSaveOptions && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-stone-200 rounded-lg shadow-lg z-10">
                  <div className="py-1">
                    {['implementation', 'analysis', 'research', 'learning'].map((category) => (
                      <button
                        key={category}
                        onClick={() => handleSave(category)}
                        className="block w-full text-left px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 capitalize"
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Compare button */}
            <button className="flex items-center space-x-2 px-3 py-2 border border-indigo-300 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-50 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>Compare</span>
            </button>

            {/* Share button */}
            <button 
              onClick={() => navigator.clipboard.writeText(item.url)}
              className="flex items-center space-x-2 px-3 py-2 border border-stone-300 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
              <span>Share</span>
            </button>

            {/* Delete button */}
            <div className="relative">
              <button
                onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
                disabled={isDeleting}
                className="flex items-center space-x-2 px-3 py-2 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
              </button>

              {showDeleteConfirm && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-red-200 rounded-lg shadow-lg z-10 p-4">
                  <p className="text-sm text-stone-700 mb-4">
                    Are you sure you want to delete this item? This action cannot be undone.
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="flex-1 bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 border border-stone-300 text-stone-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* External link */}
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 text-stone-500 hover:text-stone-700 transition-colors"
          >
            <span className="text-sm font-medium">Read Full Article</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>

      {/* Click overlay for mobile */}
      {(showSaveOptions || showDeleteConfirm) && (
        <div 
          className="fixed inset-0 z-0"
          onClick={() => {
            setShowSaveOptions(false);
            setShowDeleteConfirm(false);
          }}
        />
      )}
    </div>
  );
};

export default FeedItem;